/**
 * Robust comparison function for MUI Autocomplete isOptionEqualToValue
 * Handles multiple comparison strategies to prevent "invalid value" errors
 */
export const isOptionEqualToValue = (option: any, value: any): boolean => {
  // Handle null/undefined values
  if (!value) return false;
  if (!option) return false;

  // Handle string comparison
  if (typeof option === 'string' && typeof value === 'string') {
    return option === value;
  }

  // Handle object comparison
  if (typeof option === 'object' && typeof value === 'object') {
    // Primary comparison by _id or id
    const optionId = option._id || option.id;
    const valueId = value._id || value.id;

    if (optionId && valueId) {
      return optionId === valueId;
    }

    // Fallback comparison by email (for user objects)
    if (option.email && value.email) {
      return option.email === value.email;
    }

    // Fallback comparison by code (for SKU/product objects)
    if (option.code && value.code) {
      return option.code === value.code;
    }

    // Fallback comparison by name or label
    const optionIdentifier = option.name || option.label || option.title;
    const valueIdentifier = value.name || value.label || value.title;

    if (optionIdentifier && valueIdentifier) {
      return optionIdentifier === valueIdentifier;
    }
  }

  // Fallback to strict equality
  return option === value;
};

/**
 * Specialized comparison function for user objects
 * Prioritizes email comparison for user-specific autocompletes
 */
export const isUserOptionEqualToValue = (option: any, value: any): boolean => {
  if (!value || !option) return false;

  // Primary comparison by email for users
  if (option.email && value.email) {
    return option.email === value.email;
  }

  // Fallback to standard comparison
  return isOptionEqualToValue(option, value);
};

/**
 * Specialized comparison function for SKU/Product objects
 * Prioritizes code comparison for product-specific autocompletes
 */
export const isProductOptionEqualToValue = (option: any, value: any): boolean => {
  if (!value || !option) return false;

  // Primary comparison by code for products/SKUs
  if (option.code && value.code) {
    return option.code === value.code;
  }

  // Fallback to standard comparison
  return isOptionEqualToValue(option, value);
};

/**
 * Specialized comparison function for organization objects
 * Handles organization-specific comparison logic
 */
export const isOrganizationOptionEqualToValue = (option: any, value: any): boolean => {
  if (!value || !option) return false;

  // Primary comparison by _id
  if (option._id && value._id) {
    return option._id === value._id;
  }

  // Fallback comparison by name for organizations
  if (option.name && value.name) {
    return option.name === value.name;
  }

  // Fallback to standard comparison
  return isOptionEqualToValue(option, value);
};
